// src/components/OAuthSuccess.js - Handles OAuth callback from Flask
// SECURITY: Uses secure token exchange flow - tokens never appear in URLs
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTokenManager } from '../hooks/useTokenManager';
import { API_BASE_URL } from '../config';
import logger from '../services/logger';

const OAuthSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { storeTokens } = useTokenManager();
  const [status, setStatus] = useState('processing');
  const [platform, setPlatform] = useState('');

  // Use ref to store storeTokens to avoid dependency issues (safe approach)
  const storeTokensRef = useRef(storeTokens);
  useEffect(() => {
    storeTokensRef.current = storeTokens;
  }, [storeTokens]);

  useEffect(() => {
    const handleOAuthSuccess = async () => {
      try {
        logger.debug('OAuthSuccess component mounted, parsing URL...');

        // Parse URL parameters - SECURITY: Only code and platform, no tokens
        const urlParams = new URLSearchParams(location.search);
        const platformParam = urlParams.get('platform');
        const exchangeCode = urlParams.get('code');

        logger.info(`OAuth success handler - Platform: ${platformParam}`);

        // Validate required parameters
        if (!platformParam || !exchangeCode) {
          logger.error('Missing required OAuth parameters');
          setStatus('error');
          return;
        }

        setPlatform(platformParam);
        logger.progress('Exchanging secure code for tokens...');

        // SECURITY: Exchange the one-time code for actual tokens via secure API call
        const response = await fetch(`${API_BASE_URL}/api/oauth/exchange`, {
          method: 'POST',
          credentials: 'include', // Include session cookies
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: exchangeCode })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          logger.error('Token exchange failed:', errorData.error || response.status);
          setStatus('error');
          return;
        }

        const tokenData = await response.json();

        if (!tokenData.success || !tokenData.access_token) {
          logger.error('Invalid token response');
          setStatus('error');
          return;
        }

        logger.success(`Secure token exchange successful for ${tokenData.platform}`);

        // Store tokens in localStorage and start auto-refresh timers
        storeTokensRef.current(
          tokenData.platform,
          tokenData.access_token,
          tokenData.refresh_token,
          tokenData.expires_in || 3600
        );

        logger.success(`${tokenData.platform} tokens stored successfully`);
        setStatus('success');

        // Clear URL to remove the exchange code from history
        window.history.replaceState({}, document.title, '/oauth-success');

        // Redirect to platforms page after brief success message
        setTimeout(() => {
          navigate(`/platforms?connected=${tokenData.platform}`, { replace: true });
        }, 2000);

      } catch (error) {
        logger.error('Error processing OAuth success:', error);
        setStatus('error');
      }
    };

    handleOAuthSuccess();
  }, [location.search, navigate]); // Removed storeTokens dependency - call it directly

  // Handle error cases - redirect after delay
  useEffect(() => {
    if (status === 'error') {
      setTimeout(() => {
        navigate('/platforms?error=oauth_processing_failed', { replace: true });
      }, 3000);
    }
  }, [status, navigate]);

  return (
    <div className="oauth-success-page">
      <div className="page-background"></div>
      
      <div className="oauth-success-content">
        <div className="oauth-success-card">
          {status === 'processing' && (
            <>
              <div className="loading-spinner">
                <div className="loader-spinner"></div>
              </div>
              <h2>Processing OAuth Success...</h2>
              <p>Setting up your {platform || 'platform'} connection securely.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="success-icon">✅</div>
              <h2>{platform.charAt(0).toUpperCase() + platform.slice(1)} Connected!</h2>
              <p>Your tokens have been stored securely with auto-refresh enabled.</p>
              <p className="redirect-notice">Redirecting you to the platforms page...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="error-icon">❌</div>
              <h2>Connection Failed</h2>
              <p>There was an issue processing your OAuth connection.</p>
              <p className="redirect-notice">Redirecting you back to try again...</p>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .oauth-success-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .page-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          z-index: -1;
        }

        .oauth-success-content {
          text-align: center;
          z-index: 1;
        }

        .oauth-success-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          padding: 48px 32px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          max-width: 400px;
          margin: 0 auto;
        }

        .loading-spinner {
          margin-bottom: 24px;
        }

        .loader-spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        .success-icon, .error-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        h2 {
          color: #333;
          margin-bottom: 16px;
          font-weight: 600;
        }

        p {
          color: #666;
          margin-bottom: 12px;
          line-height: 1.6;
        }

        .redirect-notice {
          font-size: 14px;
          color: #888;
          font-style: italic;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OAuthSuccess;